import { useState, useRef, useEffect } from "react";
import { C } from "./re-colors.ts";
import { BANK, PERSONAS, NAV_TABS, _auditLog, BOOK_OF_BUSINESS, LENDING_PIPELINE, EXCEPTIONS, TEST_DATA } from "./re-data.ts";
import { TaskQueue, ScenarioModeler, PeerBenchmarks, ExamReadiness, AuditLogView, BoardPackage, SubBar, ExceptionQueue, LendingPipelineView, FraudDisputeQueue, ITTicketPanel, BCPDashboard, WatchlistPanel, MortgagePipelinePanel, WealthAccountsPanel, ALCOToolsPanel, IntradayCashPanel, VendorRiskPanel, LoanPricingPanel } from "./re-panels.tsx";
import { ChatPanel } from "./re-ai.tsx";
import { BookOfBusiness } from "./re-book.tsx";
import { DashboardContent } from "./re-dashboard.tsx";
import { EscalateModal } from "./re-modals.tsx";
import { TestDataPanel } from "./re-testdata.tsx";
import { PitchDeck, ExecutiveBrochure } from "./re-pitch.tsx";
import { ClientStoryPage } from "./re-story.tsx";
import { useIsMobile } from "./re-components.tsx";

// ── SEARCH SOURCES ────────────────────────────────────────────────
function buildSearchIndex() {
  const results: { type: string; icon: string; primary: string; detail: string; tab: string }[] = [];
  BOOK_OF_BUSINESS.forEach(c => results.push({
    type: "Client",     icon: "🏢",
    primary: c.name,
    detail: `${c.type} · ${c.city} · ${c.rm} · Score ${c.score}`,
    tab: "book",
  }));
  LENDING_PIPELINE.forEach(l => results.push({
    type: "Pipeline",   icon: "📝",
    primary: l.borrower,
    detail: `${l.id} · ${l.type} · $${(l.amount / 1e6).toFixed(1)}M · ${l.stage}`,
    tab: "pipeline",
  }));
  EXCEPTIONS.forEach(e => results.push({
    type: "Exception",  icon: "🔴",
    primary: e.customer,
    detail: `${e.type} · ${e.category} · $${e.amount.toLocaleString()} · ${e.severity}`,
    tab: "exceptions",
  }));
  TEST_DATA.criticizedAssets.credits.forEach((c: any) => results.push({
    type: "Criticized",  icon: "⚠️",
    primary: c.name,
    detail: `${c.type} · $${(c.bal / 1e6).toFixed(1)}M · ${c.cls}`,
    tab: "dashboard",
  }));
  TEST_DATA.sba.leads.forEach((l: any) => results.push({
    type: "SBA Lead",   icon: "🏛️",
    primary: l.name,
    detail: `${l.type} · $${(l.amt / 1e3).toFixed(0)}K · ${l.status}`,
    tab: "dashboard",
  }));
  return results;
}
const SEARCH_INDEX = buildSearchIndex();
const AUTH_STORAGE_KEY = "regionaledge-auth-v1";
const VALID_USERNAME = "regionaledge";
const VALID_PASSWORD = "regionaledg3";

type RouteMode = "app" | "demo";

function getInitialRouteMode(): RouteMode {
  if (typeof window === "undefined") return "app";
  const hash = window.location.hash.toLowerCase();
  return hash === "#/demo" || hash === "#demo" ? "demo" : "app";
}

function setRouteMode(mode: RouteMode) {
  if (typeof window === "undefined") return;
  const nextHash = mode === "demo" ? "#/demo" : "#/";
  if (window.location.hash !== nextHash) window.location.hash = nextHash;
}

function hasActiveAccess() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(AUTH_STORAGE_KEY) === "ok";
  } catch {
    return false;
  }
}

// ── GLOBAL SEARCH OVERLAY ─────────────────────────────────────────
function SearchOverlay({
  onClose, onNavigate,
}: {
  onClose: () => void;
  onNavigate: (tab: string) => void;
}) {
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const results = q.trim().length >= 2
    ? SEARCH_INDEX.filter(r =>
        r.primary.toLowerCase().includes(q.toLowerCase()) ||
        r.detail.toLowerCase().includes(q.toLowerCase())
      ).slice(0, 20)
    : [];

  const groups = Array.from(new Set(results.map(r => r.type)));

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: "fixed", inset: 0, zIndex: 700, background: "rgba(20,15,10,.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 80 }}
    >
      <div style={{ width: "min(92vw,640px)", background: C.card, borderRadius: 16, boxShadow: "0 24px 80px rgba(20,15,10,.4)", border: `1px solid ${C.border}`, overflow: "hidden" }}>
        {/* Input */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>🔍</span>
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search clients, pipeline, exceptions, assets…"
            style={{ flex: 1, fontSize: 15, border: "none", outline: "none", background: "none", color: C.text }}
          />
          <button onClick={onClose} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 8px", fontSize: 10, color: C.muted, cursor: "pointer" }}>ESC</button>
        </div>

        {/* Results */}
        <div style={{ maxHeight: 420, overflowY: "auto" }}>
          {q.trim().length < 2 && (
            <div style={{ padding: "32px 18px", textAlign: "center", color: C.dim, fontSize: 13 }}>
              Type 2+ characters to search across clients, loans, exceptions, and more
            </div>
          )}
          {q.trim().length >= 2 && results.length === 0 && (
            <div style={{ padding: "32px 18px", textAlign: "center", color: C.dim, fontSize: 13 }}>No results for "{q}"</div>
          )}
          {groups.map(group => (
            <div key={group}>
              <div style={{ padding: "8px 18px 4px", fontSize: 10, fontWeight: 800, color: C.dim, textTransform: "uppercase", letterSpacing: .8, borderTop: `1px solid ${C.border}` }}>{group}</div>
              {results.filter(r => r.type === group).map((r, i) => (
                <button
                  key={i}
                  onClick={() => { onNavigate(r.tab); onClose(); }}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 18px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = C.surface}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = "none"}
                >
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{r.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{r.primary}</div>
                    <div style={{ fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.detail}</div>
                  </div>
                  <span style={{ fontSize: 10, color: C.dim, flexShrink: 0 }}>→ {r.tab}</span>
                </button>
              ))}
            </div>
          ))}
        </div>

        {results.length > 0 && (
          <div style={{ padding: "8px 18px", borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.dim }}>
            {results.length} result{results.length !== 1 ? "s" : ""} — click to navigate
          </div>
        )}
      </div>
    </div>
  );
}

// ── NOTIFICATION BELL DROPDOWN ────────────────────────────────────
function AlertBell({
  escalations, pid, onDismiss, onGoToDash,
}: {
  escalations: any[]; pid: string;
  onDismiss: (idx: number) => void;
  onGoToDash: () => void;
}) {
  const [open, setOpen] = useState(false);
  const my = escalations.map((e, i) => ({ ...e, _idx: i })).filter(e => e.to === pid);
  if (my.length === 0) return null;

  const prioColor: Record<string, string> = { critical: C.burgundy, high: C.gold, medium: C.rust };

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: "none", border: "none", cursor: "pointer", position: "relative", padding: "2px 4px" }}
      >
        <span style={{ fontSize: 20 }}>🔔</span>
        <div style={{ position: "absolute", top: -2, right: -2, width: 17, height: 17, borderRadius: "50%", background: C.burgundy, border: "2px solid rgba(123,28,46,1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: "#fff" }}>
          {my.length}
        </div>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 299 }} />
          <div style={{ position: "absolute", right: 0, top: 36, width: 340, background: C.card, borderRadius: 12, boxShadow: "0 12px 48px rgba(20,15,10,.3)", border: `1px solid ${C.border}`, zIndex: 300, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: C.burgundy, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>📨 {my.length} Alert{my.length > 1 ? "s" : ""}</span>
              <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,.7)", cursor: "pointer", fontSize: 14 }}>✕</button>
            </div>
            {my.map((e, i) => (
              <div key={i} style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, background: C.card }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: prioColor[e.priority] || C.muted, textTransform: "uppercase" }}>{e.priority}</span>
                  <span style={{ fontSize: 10, color: C.dim }}>from {e.from}</span>
                </div>
                <div style={{ fontSize: 12, color: C.text, marginBottom: 8, lineHeight: 1.5 }}>{e.msg}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { onGoToDash(); setOpen(false); }} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 7, border: `1px solid ${C.burgundy}`, color: C.burgundy, background: C.burgundyBg, cursor: "pointer", fontWeight: 700 }}>View</button>
                  <button onClick={() => onDismiss(e._idx)} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 7, border: `1px solid ${C.border}`, color: C.muted, background: "none", cursor: "pointer" }}>Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── APP SHELL ─────────────────────────────────────────────────────
function AccessGate({
  routeMode,
  onUnlock,
}: {
  routeMode: RouteMode;
  onUnlock: () => void;
}) {
  const isMobile = useIsMobile();
  const userRef = useRef<HTMLInputElement>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    userRef.current?.focus();
  }, []);

  function submitAccess(e?: React.FormEvent) {
    e?.preventDefault();
    if (username === VALID_USERNAME && password === VALID_PASSWORD) {
      try {
        window.localStorage.setItem(AUTH_STORAGE_KEY, "ok");
      } catch {}
      setError("");
      onUnlock();
      return;
    }
    setError("Incorrect username or password.");
  }

  const demoUrl = typeof window !== "undefined"
    ? `${window.location.origin}${window.location.pathname}#/demo`
    : "#/demo";

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #F3ECE5 0%, #FAF7F4 30%, #FAF7F4 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 18 : 28 }}>
      <div style={{ width: "min(100%, 1100px)", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.12fr .88fr", background: C.card, border: `1px solid ${C.border}`, borderRadius: 28, overflow: "hidden", boxShadow: "0 30px 80px rgba(42,31,24,.12)" }}>
        <div style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2D5D7C 48%, #7B1C2E 100%)", color: "#fff", padding: isMobile ? "26px 22px" : "40px 36px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -70, right: -30, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
          <div style={{ position: "absolute", bottom: -90, left: 50, width: 240, height: 240, borderRadius: "50%", background: "rgba(240,200,66,.08)" }} />
          <div style={{ position: "relative" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 999, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.14)", marginBottom: 18 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 11 }}>RE</div>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: .2 }}>RegionalEdge</span>
            </div>
            <div style={{ fontSize: isMobile ? 30 : 42, lineHeight: 1.05, fontWeight: 900, letterSpacing: -1.2, maxWidth: 520, marginBottom: 16 }}>
              Secure access for the full platform and the 2-minute demo.
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.7, color: "rgba(255,255,255,.82)", maxWidth: 560, marginBottom: 22 }}>
              One login unlocks the complete intelligence hub plus a shareable demo route designed for fast executive walkthroughs.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12 }}>
              {[
                { label: "Protected App", value: "All personas, workflows, and overlays" },
                { label: "2-Minute Demo URL", value: routeMode === "demo" ? "Loaded now via #/demo" : "Available at #/demo" },
                { label: "Fast Access", value: "Single credential pair for every reviewer" },
              ].map((item) => (
                <div key={item.label} style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.14)", borderRadius: 16, padding: 14 }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: .8, color: "rgba(255,255,255,.56)", marginBottom: 6 }}>{item.label}</div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, fontWeight: 700 }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ padding: isMobile ? "24px 20px" : "40px 36px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: C.text, letterSpacing: -.5, marginBottom: 8 }}>Sign in</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              Use the assigned credentials to access the full application or the dedicated demo experience.
            </div>
          </div>

          <form onSubmit={submitAccess} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: .7 }}>Username</span>
              <input
                ref={userRef}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                style={{ height: 46, borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface, padding: "0 14px", fontSize: 14, color: C.text, outline: "none" }}
              />
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: .7 }}>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                style={{ height: 46, borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface, padding: "0 14px", fontSize: 14, color: C.text, outline: "none" }}
              />
            </label>

            {error && (
              <div style={{ borderRadius: 12, border: `1px solid ${C.burgundyLight}`, background: C.burgundyBg, color: C.burgundy, padding: "11px 12px", fontSize: 12, fontWeight: 700 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              style={{ height: 48, borderRadius: 14, border: "none", background: C.navy, color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", marginTop: 4 }}
            >
              Open RegionalEdge
            </button>
          </form>

          <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 11, color: C.dim, textTransform: "uppercase", letterSpacing: .8 }}>Demo URL</div>
            <div style={{ fontSize: 13, color: C.text, fontWeight: 700, wordBreak: "break-all" }}>{demoUrl}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AppShell({
  onLogout,
  onOpenDemo,
}: {
  onLogout: () => void;
  onOpenDemo: () => void;
}) {
  const [pid, setPid] = useState("ceo");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [chatOpen, setChatOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [testDataOpen, setTestDataOpen] = useState(false);
  const [pitchOpen, setPitchOpen] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [escalations, setEscalations] = useState<any[]>([]);
  const [auditLog, setAuditLog] = useState<any[]>([]);
  const isMobile = useIsMobile();

  const persona = PERSONAS.find(p => p.id === pid)!;
  const tabs = NAV_TABS[pid] || [];

  function onLog(action: string, detail: string, category = "General") {
    const entry = {
      id:       Date.now(),
      user:     persona.name,
      action,
      detail,
      category,
      time:     new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
      date:     "Mar 2, 2026",
    };
    _auditLog.unshift(entry);
    setAuditLog([..._auditLog]);
  }

  function handleEscalate(data: any) {
    const target = PERSONAS.find(p => p.id === data.to);
    setEscalations(e => [...e, data]);
    onLog("Escalated alert", `To ${target?.name}: ${data.msg.substring(0, 40)}…`, "Escalation");
  }

  function dismissAlert(idx: number) {
    setEscalations(e => e.filter((_, i) => i !== idx));
  }

  function switchPersona(newPid: string) {
    setPid(newPid);
    setActiveTab("dashboard");
  }

  function navigate(tab: string) {
    setActiveTab(tab);
    onLog("Search navigation", `Navigated to ${tab}`, "General");
  }

  function renderTab() {
    const dashView = <DashboardContent pid={pid} persona={persona} onLog={onLog} onEscalate={() => setEscalateOpen(true)} escalations={escalations} onNavTasks={() => setActiveTab("tasks")} />;
    switch (activeTab) {
      case "tasks":         return <TaskQueue           pid={pid} persona={persona} onLog={onLog} />;
      case "peers":         return <PeerBenchmarks />;
      case "scenario":      return <ScenarioModeler />;
      case "exam":          return <ExamReadiness />;
      case "board":         return <BoardPackage        persona={persona} onLog={onLog} />;
      case "audit":         return <AuditLogView />;
      case "book":          return <BookOfBusiness      onLog={onLog} />;
      case "pipeline":      return <LendingPipelineView onLog={onLog} />;
      case "exceptions":    return <ExceptionQueue      onLog={onLog} />;
      case "fraud":         return <FraudDisputeQueue   onLog={onLog} />;
      case "ittickets":     return <ITTicketPanel       onLog={onLog} />;
      case "bcp":           return <BCPDashboard />;
      case "watchlist":     return <WatchlistPanel      onLog={onLog} />;
      case "loanfiles":     return <WatchlistPanel      onLog={onLog} />;
      case "mortpipeline":  return <MortgagePipelinePanel onLog={onLog} />;
      case "wealthaccounts":return <WealthAccountsPanel onLog={onLog} />;
      case "alco":          return <ALCOToolsPanel      onLog={onLog} />;
      case "branchdetail":  return dashView;
      case "depoprofit":    return <DashboardContent    pid={pid} persona={persona} onLog={onLog} onEscalate={() => setEscalateOpen(true)} escalations={escalations} onNavTasks={() => setActiveTab("tasks")} initialModal="depoprofit" />;
      case "cashposition":  return <IntradayCashPanel   onLog={onLog} />;
      case "vendors":       return <VendorRiskPanel     onLog={onLog} />;
      case "loanpricing":   return <LoanPricingPanel    onLog={onLog} />;
      case "hmdalar":       return <DashboardContent    pid={pid} persona={persona} onLog={onLog} onEscalate={() => setEscalateOpen(true)} escalations={escalations} onNavTasks={() => setActiveTab("tasks")} initialModal="hmdalar" />;
      case "fairlending":   return <DashboardContent    pid={pid} persona={persona} onLog={onLog} onEscalate={() => setEscalateOpen(true)} escalations={escalations} onNavTasks={() => setActiveTab("tasks")} initialModal="fairlending" />;
      case "derivatives":   return <DashboardContent    pid={pid} persona={persona} onLog={onLog} onEscalate={() => setEscalateOpen(true)} escalations={escalations} onNavTasks={() => setActiveTab("tasks")} initialModal="derivatives" />;
      case "modelgov":      return <DashboardContent    pid={pid} persona={persona} onLog={onLog} onEscalate={() => setEscalateOpen(true)} escalations={escalations} onNavTasks={() => setActiveTab("tasks")} initialModal="modelgov" />;
      default:              return dashView;
    }
  }

  const myAlerts = escalations.filter(e => e.to === pid).length;

  return (
    <div style={{ fontFamily: "-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',sans-serif", background: C.bg, minHeight: "100vh", color: C.text }}>

      {/* ── TOP NAV ── */}
      <div style={{ background: C.burgundy, borderBottom: `1px solid ${C.burgundyDark}`, position: "sticky", top: 0, zIndex: 200, boxShadow: "0 2px 12px rgba(92,18,32,.25)" }}>
        <div style={{ maxWidth: 1440, margin: "0 auto", padding: isMobile ? "0 12px" : "0 24px" }}>
          <div style={{ display: "flex", alignItems: "center", height: 54, gap: isMobile ? 8 : 16 }}>

            {/* Logo */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.25)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 12, color: "#fff" }}>VF</div>
              {!isMobile && (
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: "#fff", letterSpacing: -.3 }}>RegionalEdge <span style={{ color: C.goldLight, fontStyle: "italic" }}>AI</span></div>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,.55)", letterSpacing: .5, textTransform: "uppercase" }}>{BANK.institution}</div>
                </div>
              )}
            </div>

            {/* Persona Switcher — scrollable on mobile */}
            <div style={{ display: "flex", gap: 1, background: "rgba(0,0,0,.2)", borderRadius: 10, padding: 3, flex: 1, overflowX: "auto", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any }}>
              {PERSONAS.map(p => (
                <button
                  key={p.id}
                  onClick={() => switchPersona(p.id)}
                  style={{ flexShrink: 0, background: pid === p.id ? "rgba(255,255,255,.15)" : "none", border: pid === p.id ? "1px solid rgba(255,255,255,.2)" : "1px solid transparent", borderRadius: 8, padding: isMobile ? "5px 8px" : "5px 4px", cursor: "pointer", color: pid === p.id ? "#fff" : "rgba(255,255,255,.55)", fontWeight: pid === p.id ? 700 : 500, fontSize: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, minWidth: isMobile ? 44 : undefined }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 0 : 3 }}>
                    <div style={{ width: 16, height: 16, borderRadius: 5, background: pid === p.id ? "rgba(255,255,255,.25)" : "rgba(255,255,255,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800, color: "#fff", flexShrink: 0 }}>{p.initials}</div>
                    {!isMobile && <span style={{ fontSize: 10 }}>{p.label}</span>}
                  </div>
                  {!isMobile && <span style={{ fontSize: 9, opacity: .65, fontWeight: 400 }}>{p.name.split(" ")[0]}</span>}
                </button>
              ))}
            </div>

            {/* Search button */}
            <button
              onClick={() => setSearchOpen(true)}
              style={{ background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.2)", borderRadius: 20, padding: isMobile ? "7px 10px" : "7px 14px", cursor: "pointer", color: "rgba(255,255,255,.8)", fontSize: 13, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}
            >
              🔍{!isMobile && <span style={{ fontSize: 11, fontWeight: 600 }}>Search</span>}
            </button>

            {/* Test Data button — hidden on mobile */}
            {!isMobile && (
              <button
                onClick={() => setTestDataOpen(o => !o)}
                style={{ background: testDataOpen ? "rgba(255,255,255,.2)" : "rgba(255,255,255,.08)", color: testDataOpen ? "#fff" : "rgba(255,255,255,.65)", border: `1px solid ${testDataOpen ? "rgba(255,255,255,.35)" : "rgba(255,255,255,.15)"}`, borderRadius: 20, padding: "7px 14px", cursor: "pointer", fontWeight: 700, fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}
              >
                🗄️ Test Data
              </button>
            )}

            {/* Executive Brochure / PDF button — hidden on mobile */}
            {!isMobile && (
              <button
                onClick={() => setBrochureOpen(true)}
                style={{ background: "rgba(255,255,255,.08)", color: "rgba(255,255,255,.65)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 20, padding: "7px 14px", cursor: "pointer", fontWeight: 700, fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}
              >
                📄 Overview
              </button>
            )}

            {/* Capabilities / Pitch button — hidden on mobile */}
            {!isMobile && (
              <button
                onClick={onOpenDemo}
                style={{ background: "rgba(255,255,255,.08)", color: "rgba(255,255,255,.65)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 20, padding: "7px 14px", cursor: "pointer", fontWeight: 700, fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}
              >
                🎬 Demo Story
              </button>
            )}

            {!isMobile && (
              <button
                onClick={() => setPitchOpen(o => !o)}
                style={{ background: pitchOpen ? "rgba(255,255,255,.2)" : "rgba(255,255,255,.08)", color: pitchOpen ? "#fff" : "rgba(255,255,255,.65)", border: `1px solid ${pitchOpen ? "rgba(255,255,255,.35)" : "rgba(255,255,255,.15)"}`, borderRadius: 20, padding: "7px 14px", cursor: "pointer", fontWeight: 700, fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}
              >
                🎯 Capabilities
              </button>
            )}

            {/* Date indicator — hidden on mobile */}
            {!isMobile && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "rgba(255,255,255,.5)", flexShrink: 0 }}>
                <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 6px #4ade80" }} />
                <span>{BANK.asOfDate}</span>
              </div>
            )}

            {/* Alert bell with dropdown */}
            <AlertBell
              escalations={escalations}
              pid={pid}
              onDismiss={dismissAlert}
              onGoToDash={() => setActiveTab("dashboard")}
            />

            {/* Chat toggle */}
            <button
              onClick={() => setChatOpen(!chatOpen)}
              style={{ background: chatOpen ? "rgba(255,255,255,.2)" : "rgba(255,255,255,.1)", color: "#fff", border: "1px solid rgba(255,255,255,.25)", borderRadius: 20, padding: "7px 12px", cursor: "pointer", fontWeight: 700, fontSize: 11, display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}
            >
              💬{!isMobile && <span> {chatOpen ? "Close" : "AI Agent"}</span>}
            </button>
          </div>
        </div>

        {/* Sub-bar — hidden on mobile */}
        {!isMobile && <SubBar pid={pid} persona={persona} />}

        {/* Tab bar — scrollable on mobile */}
        <div style={{ background: C.burgundyDark, borderTop: "1px solid rgba(255,255,255,.06)", padding: isMobile ? "0 8px" : "0 24px", display: "flex", gap: 2, overflowX: "auto", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any }}>
          {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            const badge = tab.id === "dashboard" && myAlerts > 0 ? myAlerts : 0;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 12px", background: isActive ? "rgba(255,255,255,.12)" : "transparent", border: "none", borderBottom: isActive ? `2px solid ${C.goldLight}` : "2px solid transparent", cursor: "pointer", color: isActive ? "#fff" : "rgba(255,255,255,.5)", fontWeight: isActive ? 700 : 400, fontSize: 11, whiteSpace: "nowrap", flexShrink: 0 }}
              >
                <span style={{ fontSize: 12 }}>{tab.icon}</span>
                <span>{tab.label}</span>
                {badge > 0 && (
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: C.burgundy, border: "1px solid rgba(255,255,255,.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800, color: "#fff", marginLeft: 2 }}>{badge}</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ display: "flex", maxWidth: 1440, margin: "0 auto" }}>
        <div style={{ flex: 1, minWidth: 0, padding: isMobile ? "14px 12px" : "22px 24px" }}>{renderTab()}</div>
        {chatOpen && !isMobile && (
          <div style={{ width: 356, flexShrink: 0, height: "calc(100vh - 130px)", position: "sticky", top: 130, overflow: "hidden" }}>
            <ChatPanel persona={persona} onLog={onLog} />
          </div>
        )}
        {chatOpen && isMobile && (
          <div style={{ position: "fixed", inset: 0, zIndex: 500, background: C.bg, overflow: "hidden", paddingTop: 0 }}>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 16px", background: C.burgundy }}>
              <button onClick={() => setChatOpen(false)} style={{ background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.25)", borderRadius: 8, color: "#fff", cursor: "pointer", padding: "6px 14px", fontWeight: 700, fontSize: 12 }}>✕ Close</button>
            </div>
            <div style={{ height: "calc(100vh - 52px)", overflow: "hidden" }}>
              <ChatPanel persona={persona} onLog={onLog} />
            </div>
          </div>
        )}
      </div>

      {/* ── ESCALATION MODAL ── */}
      {escalateOpen && (
        <EscalateModal
          onClose={() => setEscalateOpen(false)}
          fromPersona={persona}
          personas={PERSONAS}
          onEscalate={handleEscalate}
        />
      )}

      {/* ── TEST DATA PANEL ── */}
      {testDataOpen && <TestDataPanel onClose={() => setTestDataOpen(false)} />}

      {/* ── PITCH DECK / CAPABILITIES ── */}
      {pitchOpen && <PitchDeck onClose={() => setPitchOpen(false)} />}
      {brochureOpen && <ExecutiveBrochure onClose={() => setBrochureOpen(false)} />}

      {/* ── SEARCH OVERLAY ── */}
      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} onNavigate={navigate} />}

      {/* ── GLOBAL STYLES ── */}
      <style>{`
        * { box-sizing: border-box; margin: 0; }
        @keyframes dot {
          0%, 100% { opacity: .2; transform: scale(.8); }
          50%       { opacity: 1;  transform: scale(1.1); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        ::-webkit-scrollbar       { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: ${C.surface}; }
        ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 4px; }
        input::placeholder        { color: ${C.dim}; }
        textarea::placeholder     { color: ${C.dim}; }
        button:focus              { outline: none; }
        .kpi-grid { display: grid; }
        .board-grid { display: grid; }
        .peer-summary-grid { display: grid; }
        @media (max-width: 720px) {
          .kpi-grid       { grid-template-columns: repeat(2, 1fr) !important; }
          .board-grid     { grid-template-columns: 1fr !important; }
          .peer-summary-grid { grid-template-columns: 1fr !important; }
          .briefing-grid  { grid-template-columns: 1fr !important; }
          .scenario-grid  { grid-template-columns: 1fr !important; }
          .peers-table    { font-size: 11px !important; }
        }
      `}</style>
    </div>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(hasActiveAccess);
  const [routeMode, setCurrentRouteMode] = useState<RouteMode>(getInitialRouteMode);

  useEffect(() => {
    const syncRoute = () => setCurrentRouteMode(getInitialRouteMode());
    window.addEventListener("hashchange", syncRoute);
    syncRoute();
    return () => window.removeEventListener("hashchange", syncRoute);
  }, []);

  function unlock() {
    setAuthenticated(true);
  }

  function openDemoRoute() {
    setRouteMode("demo");
    setCurrentRouteMode("demo");
  }

  function openAppRoute() {
    setRouteMode("app");
    setCurrentRouteMode("app");
  }

  function logout() {
    try {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {}
    setAuthenticated(false);
    openAppRoute();
  }

  if (!authenticated) {
    return <AccessGate routeMode={routeMode} onUnlock={unlock} />;
  }

  if (routeMode === "demo") {
    return <ClientStoryPage onClose={openAppRoute} />;
  }

  return <AppShell onLogout={logout} onOpenDemo={openDemoRoute} />;
}
